/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { dbRealtime, ref, get, update, remove } from '../environments/firebase';
import { jsPDF } from 'jspdf';
import { useAuth } from '../contexts/AuthContext';

type GungrahanData = {
  [name: string]: {
    points: string[]; 
    createdAt?: number;
  };
};

type MergedGungrahanData = {
  [normalizedName: string]: {
    originalNames: string[];
    points: string[];
  };
};

export default function GungrahanReport() {
  const [originalData, setOriginalData] = useState<GungrahanData>({});
  const [mergedData, setMergedData] = useState<MergedGungrahanData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeCandidates, setMergeCandidates] = useState<{name: string, similarNames: string[]}[]>([]);
  const [userMergeChoices, setUserMergeChoices] = useState<Record<string, string>>({});
  const [editingEntry, setEditingEntry] = useState<string | null>(null);
  const [editedEntry, setEditedEntry] = useState<{
    originalNames: string[];
    points: string[];
  } | null>(null);
  const [newPoint, setNewPoint] = useState('');
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;
      
      setIsLoading(true);
      try {
        const snapshot = await get(ref(dbRealtime, `users/${currentUser.uid}/gungrahan`));
        if (snapshot.exists()) {
          const data = snapshot.val();
          setOriginalData(data);
          checkForMergeCandidates(data);
        } else {
          setOriginalData({});
          setMergedData({});
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  const checkForMergeCandidates = (data: GungrahanData) => {
    const names = Object.keys(data);
    const candidates: {name: string, similarNames: string[]}[] = [];
    const processedNames = new Set<string>();

    names.forEach(name => {
      if (processedNames.has(name)) return;

      const normalized = name.toLowerCase().replace(/[^a-z0-9]/g, '');
      const similar = names.filter(n => 
        n !== name && 
        !processedNames.has(n) && 
        n.toLowerCase().replace(/[^a-z0-9]/g, '') === normalized
      );

      if (similar.length > 0) {
        candidates.push({
          name,
          similarNames: similar
        });
        processedNames.add(name);
        similar.forEach(n => processedNames.add(n));
      }
    });

    if (candidates.length > 0) {
      setMergeCandidates(candidates);
      setShowMergeDialog(true);
    } else {
      processDataForDisplay(data);
    }
  };

  const handleMergeChoice = (mainName: string, chosenName: string) => {
    setUserMergeChoices(prev => ({
      ...prev,
      [mainName]: chosenName
    }));
  };

  const applyMergeChoices = () => {
    const newData = {...originalData};
    const merged: MergedGungrahanData = {};

    Object.entries(userMergeChoices).forEach(([mainName, chosenName]) => {
      if (!newData[mainName] || !newData[chosenName]) return;

      const normalized = chosenName.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (!merged[normalized]) {
        merged[normalized] = {
          originalNames: [chosenName, mainName],
          points: [...newData[chosenName].points, ...newData[mainName].points]
        };
      } else {
        merged[normalized].originalNames.push(mainName);
        merged[normalized].points.push(...newData[mainName].points);
      }

      delete newData[mainName];
      delete newData[chosenName];
    });

    Object.entries(newData).forEach(([name, entry]) => {
      const normalized = name.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (!merged[normalized]) {
        merged[normalized] = {
          originalNames: [name],
          points: [...entry.points]
        };
      }
    });

    setMergedData(merged);
    setShowMergeDialog(false);
  };

  const processDataForDisplay = (data: GungrahanData) => {
    const processed: MergedGungrahanData = {};
    
    Object.entries(data).forEach(([name, entry]) => {
      // Ensure entry exists and has points
      if (!entry || !entry.points || !Array.isArray(entry.points)) {
        console.warn(`Invalid entry for ${name}:`, entry);
        return; // Skip this entry
      }
  
      const normalized = name.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (!processed[normalized]) {
        processed[normalized] = {
          originalNames: [name],
          points: [...entry.points] // Create a new array
        };
      } else {
        processed[normalized].originalNames.push(name);
        processed[normalized].points.push(...entry.points);
      }
    });
  
    setMergedData(processed);
  };

  const startEditing = (originalName: string) => {
    const entry = Object.values(mergedData).find(e => e.originalNames.includes(originalName));
    if (entry) {
      setEditingEntry(originalName);
      setEditedEntry({
        originalNames: [...entry.originalNames],
        points: [...entry.points]
      });
    }
  };

  const cancelEditing = () => {
    setEditingEntry(null);
    setEditedEntry(null);
  };

  const saveEditedEntry = async () => {
    if (!editingEntry || !editedEntry || !currentUser) return;

    try {
      // First, delete all old entries
      const entry = Object.values(mergedData).find(e => e.originalNames.includes(editingEntry));
      if (!entry) return;

      for (const name of entry.originalNames) {
        await remove(ref(dbRealtime, `users/${currentUser.uid}/gungrahan/${name}`));
      }

      // Then create new entries with updated data
      for (const name of editedEntry.originalNames) {
        await update(ref(dbRealtime, `users/${currentUser.uid}/gungrahan/${name}`), {
          points: editedEntry.points,
          createdAt: Date.now()
        });
      }

      // Refresh data
      const snapshot = await get(ref(dbRealtime, `users/${currentUser.uid}/gungrahan`));
      if (snapshot.exists()) {
        const data = snapshot.val();
        setOriginalData(data);
        processDataForDisplay(data);
      }

      setEditingEntry(null);
      setEditedEntry(null);
    } catch (error) {
      console.error('Error updating entry:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  const deleteEntry = async (originalName: string) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    if (!currentUser) return;

    try {
      const entryKey = originalName.trim()
                         .toLowerCase()
                         .replace(/[^a-z0-9]/g, '_');
      
      await remove(ref(dbRealtime, `users/${currentUser.uid}/gungrahan/${entryKey}`));

      const snapshot = await get(ref(dbRealtime, `users/${currentUser.uid}/gungrahan`));
      if (snapshot.exists()) {
        const data = snapshot.val();
        setOriginalData(data);
        processDataForDisplay(data);
      } else {
        setOriginalData({});
        setMergedData({});
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Error deleting entry: ' + error);
    }
  };

  const addNewPoint = () => {
    if (newPoint.trim() && editedEntry) {
      setEditedEntry({
        ...editedEntry,
        points: [...editedEntry.points, newPoint.trim()]
      });
      setNewPoint('');
    }
  };

  const removePoint = (index: number) => {
    if (!editedEntry) return;
    const newPoints = [...editedEntry.points];
    newPoints.splice(index, 1);
    setEditedEntry({
      ...editedEntry,
      points: newPoints
    });
  };

  const updatePoint = (index: number, value: string) => {
    if (!editedEntry) return;
    const newPoints = [...editedEntry.points];
    newPoints[index] = value;
    setEditedEntry({
      ...editedEntry,
      points: newPoints
    });
  };

  const updateOriginalName = (index: number, value: string) => {
    if (!editedEntry) return;
    const newNames = [...editedEntry.originalNames];
    newNames[index] = value;
    setEditedEntry({
      ...editedEntry,
      originalNames: newNames
    });
  };

  const addNewNameVariant = () => {
    if (!editedEntry) return;
    setEditedEntry({
      ...editedEntry,
      originalNames: [...editedEntry.originalNames, '']
    });
  };

  const removeNameVariant = (index: number) => {
    if (!editedEntry) return;
    const newNames = [...editedEntry.originalNames];
    newNames.splice(index, 1);
    setEditedEntry({
      ...editedEntry,
      originalNames: newNames
    });
  };

  const downloadPDF = async () => {
    setIsGeneratingPDF(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = 30;
      let currentPage = 1;

      // Set default font
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(12);

      const addPageNumber = () => {
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Page ${currentPage}`, pageWidth - margin, 10);
      };

      // Title Page
      pdf.setFontSize(28);
      pdf.setTextColor(30, 58, 138);
      pdf.text('Gungrahan Report', pageWidth / 2, pageHeight / 2 - 20, { align: 'center' });
      
      pdf.setFontSize(16);
      pdf.setTextColor(75, 85, 99);
      pdf.text('Positive Qualities of Devotees', pageWidth / 2, pageHeight / 2, { align: 'center' });
      
      pdf.setFontSize(14);
      pdf.text('www.MySatsang.com', pageWidth / 2, pageHeight / 2 + 20, { align: 'center' });
      
      addPageNumber();
      currentPage++;

      // Note Page
      pdf.addPage();
      yPosition = 30;
      addPageNumber();

      pdf.setFontSize(18);
      pdf.setTextColor(30, 58, 138);
      pdf.text('Jay Swaminarayan!', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      
      const noteLines = [
        "This Gungrahan Report contains positive qualities observed in devotees.",
        "The purpose of this report is to inspire spiritual growth by recognizing",
        "and appreciating the divine qualities in others.",
        "",
        "Note:",
        "All content in this report was submitted by users of MySatsang.com.",
        "The developer holds no responsibility for the accuracy of the content.",
        "",
        "May this report serve as a reminder of the divine qualities within each of us,",
        "and inspire us to cultivate these qualities in our own lives.",
        "",
        "- MySatsang.com"
      ];

      noteLines.forEach(line => {
        if (line === "") {
          yPosition += 5;
          return;
        }
        
        const wrappedLines = pdf.splitTextToSize(line, pageWidth - 2 * margin);
        wrappedLines.forEach((wrappedLine: string) => {
          if (yPosition > pageHeight - 15) {
            pdf.addPage();
            currentPage++;
            addPageNumber();
            yPosition = 30;
          }
          pdf.text(wrappedLine, margin, yPosition);
          yPosition += 7;
        });
      });

      addPageNumber();
      currentPage++;

      // Table of Contents
      pdf.addPage();
      yPosition = 30;
      addPageNumber();

      pdf.setFontSize(18);
      pdf.setTextColor(30, 58, 138);
      pdf.text('Table of Contents', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 20;

      const tocPage = currentPage;
      const tocEntries: { name: string, page: number }[] = [];
      currentPage++;

      // Entries
      pdf.addPage();
      yPosition = 30;
      addPageNumber();

      const sortedEntries = Object.entries(mergedData)
        .sort((a, b) => a[1].originalNames[0].localeCompare(b[1].originalNames[0]));

      sortedEntries.forEach(([_, entry], index) => {
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          currentPage++;
          yPosition = 30;
          addPageNumber();
        }

        const displayName = entry.originalNames[0];
        tocEntries.push({
          name: displayName,
          page: currentPage
        });

        pdf.setFontSize(16);
        pdf.setTextColor(30, 58, 138);
        pdf.text(displayName, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 10;

        if (entry.originalNames.length > 1) {
          pdf.setFontSize(10);
          pdf.setTextColor(100, 100, 100);
          const alsoKnownAs = `(also recorded as: ${entry.originalNames.slice(1).join(', ')})`;
          pdf.text(alsoKnownAs, pageWidth / 2, yPosition, { align: 'center' });
          yPosition += 8;
        }

        yPosition += 10;

        const uniquePoints = Array.from(new Set(entry.points));
        uniquePoints.forEach(point => {
          const bulletPoint = `• ${point}`;
          const wrappedLines = pdf.splitTextToSize(bulletPoint, pageWidth - 2 * margin);
          
          wrappedLines.forEach((wrappedLine: string, i: number) => {
            if (yPosition > pageHeight - 15) {
              pdf.addPage();
              currentPage++;
              yPosition = 30;
              addPageNumber();
            }
            pdf.setFontSize(12);
            pdf.setTextColor(0, 0, 0);
            pdf.text(wrappedLine, margin + (i === 0 ? 0 : 10), yPosition);
            yPosition += 7;
          });
          
          yPosition += 2;
        });

        if (index < sortedEntries.length - 1) {
          yPosition += 10;
          pdf.setDrawColor(200, 200, 200);
          pdf.line(margin, yPosition, pageWidth - margin, yPosition);
          yPosition += 15;
        }
      });

      // Fill TOC
      pdf.setPage(tocPage);
      yPosition = 50;

      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);

      tocEntries.forEach(entry => {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          currentPage++;
          yPosition = 30;
          addPageNumber();
        }

        pdf.text(entry.name, margin, yPosition);
        pdf.text(`Page ${entry.page}`, pageWidth - margin, yPosition, { align: 'right' });
        yPosition += 10;

        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.2);
        pdf.line(
          margin + pdf.getStringUnitWidth(entry.name) * pdf.getFontSize() + 2,
          yPosition - 4,
          pageWidth - margin - pdf.getStringUnitWidth(`Page ${entry.page}`) * pdf.getFontSize() - 2,
          yPosition - 4
        );
      });

      pdf.save('gungrahan-report.pdf');
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (showMergeDialog) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-bold text-blue-800 mb-4">Merge Similar Names</h2>
          <p className="mb-6 text-gray-700">
            We found some names that might refer to the same person. Please review and choose how you'd like to merge them.
          </p>
          
          {mergeCandidates.map((candidate, idx) => (
            <div key={idx} className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-2">Which name should we use for:</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {[candidate.name, ...candidate.similarNames].map(name => (
                  <button
                    key={name}
                    onClick={() => handleMergeChoice(candidate.name, name)}
                    className={`px-3 py-1 rounded-full text-sm ${userMergeChoices[candidate.name] === name 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                  >
                    {name}
                  </button>
                ))}
              </div>
              <p className="text-sm text-gray-600">
                Selected: <span className="font-medium">
                  {userMergeChoices[candidate.name] || 'Not selected yet'}
                </span>
              </p>
            </div>
          ))}
          
          <div className="flex justify-end gap-3 mt-6">
            <button 
              onClick={() => {
                setShowMergeDialog(false);
                processDataForDisplay(originalData);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
            >
              Skip Merging
            </button>
            <button 
              onClick={applyMergeChoices}
              disabled={mergeCandidates.some(c => !userMergeChoices[c.name])}
              className={`px-4 py-2 rounded-lg text-white ${mergeCandidates.some(c => !userMergeChoices[c.name]) 
                ? 'bg-blue-300 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              Apply Merges
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <h1 className="text-2xl md:text-3xl font-bold text-blue-800">Gungrahan Report</h1>
          <button
            onClick={downloadPDF}
            disabled={isGeneratingPDF || Object.keys(mergedData).length === 0}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isGeneratingPDF ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating PDF...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Download All as PDF
              </>
            )}
          </button>
        </div>
        
        {Object.keys(mergedData).length === 0 ? (
          <div className="text-center text-gray-600 py-12 bg-white rounded-lg shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-4 text-lg">No gungrahan entries found.</p>
            <p className="text-gray-500">Please add some entries first.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(mergedData)
              .sort((a, b) => a[1].originalNames[0].localeCompare(b[1].originalNames[0]))
              .map(([_, entry]) => (
                <div key={entry.originalNames[0]} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 hover:border-blue-100 transition-all relative">
                  <div className="absolute top-4 right-4 flex gap-2">
                    {editingEntry === entry.originalNames[0] ? (
                      <>
                        <button
                          onClick={saveEditedEntry}
                          className="text-green-600 hover:text-green-800 p-1 transition-colors"
                          title="Save"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="text-red-600 hover:text-red-800 p-1 transition-colors"
                          title="Cancel"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEditing(entry.originalNames[0])}
                          className="text-blue-600 hover:text-blue-800 p-1 transition-colors"
                          title="Edit"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => deleteEntry(entry.originalNames[0])}
                          className="text-red-600 hover:text-red-800 p-1 transition-colors"
                          title="Delete"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>

                  <div className="p-5">
                    {editingEntry === entry.originalNames[0] && editedEntry ? (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <h3 className="text-sm font-medium text-gray-700">Name Variants:</h3>
                          {editedEntry.originalNames.map((name, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <input
                                type="text"
                                value={name}
                                onChange={(e) => updateOriginalName(idx, e.target.value)}
                                className="flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-300 focus:border-blue-500"
                              />
                              <button
                                onClick={() => removeNameVariant(idx)}
                                className="text-red-500 hover:text-red-700 p-1"
                                disabled={editedEntry.originalNames.length <= 1}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={addNewNameVariant}
                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                            Add another name variant
                          </button>
                        </div>

                        <div className="space-y-2">
                          <h3 className="text-sm font-medium text-gray-700">Positive Qualities:</h3>
                          <ul className="space-y-2">
                            {editedEntry.points.map((point, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="text-blue-600 mt-2">•</span>
                                <textarea
                                  value={point}
                                  onChange={(e) => updatePoint(i, e.target.value)}
                                  className="flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-300 focus:border-blue-500"
                                  rows={2}
                                />
                                <button
                                  onClick={() => removePoint(i)}
                                  className="text-red-500 hover:text-red-700 p-1 mt-2"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </li>
                            ))}
                          </ul>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newPoint}
                              onChange={(e) => setNewPoint(e.target.value)}
                              placeholder="Add new quality"
                              className="flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-300 focus:border-blue-500"
                              onKeyPress={(e) => e.key === 'Enter' && addNewPoint()}
                            />
                            <button
                              onClick={addNewPoint}
                              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-col items-center mb-4">
                          <h2 className="text-xl font-semibold text-center text-blue-700">
                            {entry.originalNames[0]}
                          </h2>
                          {entry.originalNames.length > 1 && (
                            <p className="text-sm text-gray-500 mt-1">
                              (also recorded as: {entry.originalNames.slice(1).join(', ')})
                            </p>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          {Array.from(new Set(entry.points)).map((point, i) => (
                            <div key={i} className="flex gap-2">
                              <span className="text-blue-600">•</span>
                              <p className="text-gray-800 whitespace-pre-wrap">{point}</p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}